import { useState, useEffect } from "react";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export const isPWAInstalled = (): boolean => {
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
  const isIOSStandalone = (window.navigator as any).standalone === true;

  return isStandalone || isIOSStandalone;
};

export const canInstallPWA = (): boolean => {
  return "onbeforeinstallprompt" in window;
};

export const canSuggestPWAInstallation = (): boolean => {
  return !isPWAInstalled() && canInstallPWA();
};

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const event = e as BeforeInstallPromptEvent;
      event.preventDefault();
      setDeferredPrompt(event);
    };

    window.addEventListener("beforeinstallprompt", handler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const installApp = async (): Promise<"accepted" | "dismissed" | "error"> => {
    if (!deferredPrompt) return "error";

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log("Install prompt outcome:", outcome);

      if (outcome === "accepted") {
        setDeferredPrompt(null);
      }

      return outcome;
    } catch (error) {
      console.error("Error during app installation:", error);
      return "error";
    }
  };

  return {
    installApp,
  };
};
